import { createSelector } from 'reselect';

import { businessSelectors } from 'app-components/flux/business';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import type {
  ComparisonRange,
  ReportConfiguration,
  ReportConfigurationProps,
} from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import type RootState from 'app-components/utils/redux-store/rootState';

import {
  getConfigDisplayName,
  getSavedReportBaseName,
  getSuggestedSavedReportName,
  OPEN_PERSISTENT_TYPES,
  SAVED_PERSISTENT_TYPES,
} from './reportConfigurationsConstants';
import { REDUCER_KEY } from './reportConfigurationsTypes';
import { validateComparisonRanges } from './utils/comparisonRanges';
import { getReportFieldConfig, getTimestepOptionsForSubtype } from './utils/reportFieldAvailability';

/**
 * Stable identity so `selectComparisonRanges` does not churn for configs without ranges.
 * Frozen because it is shared by every such config — one caller mutating it would poison the rest.
 */
const EMPTY_COMPARISON_RANGES: readonly ComparisonRange[] = Object.freeze([]);

export const getReportConfigurationStore = (state: Pick<RootState, REDUCER_KEY>) => state[REDUCER_KEY];

export const getIsLoading = createSelector(getReportConfigurationStore, (store) => store.isLoading);
export const getLoadPending = createSelector(getReportConfigurationStore, (store) => store.loadPending);
export const getError = createSelector(getReportConfigurationStore, (store) => store.error);

// Raw map including isDeleted tombstones. Use only when tombstones must be
// observed (e.g. the bump-handling saga, which compares dbVersion against
// tombstones to reject stale resurrections). All other consumers should use
// getResourcesById, which filters tombstones out.
export const getResourcesByIdWithTombstones = createSelector(
  getReportConfigurationStore,
  (store) => store.resourcesById,
);

export const getResourcesById = createSelector(getResourcesByIdWithTombstones, (resourcesById) =>
  resourcesById.filter((config) => !config?.isDeleted),
);

export const getLastSyncDate = createSelector(getReportConfigurationStore, (store) => store.lastSyncDate);

export const getReportConfigurationById = getResourcesById;

export const getReportConfigById = createSelector(
  getReportConfigurationById,
  (_state: unknown, props: { id: string }) => props.id,
  (reportConfigurationsById, id) => reportConfigurationsById?.get(id),
);

export const getReportConfigByUniqueIdentifier = createSelector(
  [getReportConfigurationById, (_state: unknown, props: { name: string }) => props.name],
  (configsById, name) => {
    return configsById?.find((config) => config?.get('name') === name && config.id !== undefined);
  },
);

export const getReportConfigByUniqueIdentifierWeb = createSelector(
  [getReportConfigurationById, (_state: unknown, props: { getTabName: () => string }) => props.getTabName()],
  (configsById, name) => {
    return configsById?.find((config) => config?.get('name') === name && config.id !== undefined);
  },
);

// Main selector that computes the full report field config
export const selectReportFieldConfig = createSelector(
  [
    (state, reportConfigId: string) => getReportConfigurationById(state).get(reportConfigId || ''),
    (state) => entitlementsSelectors.getBusinessEnabled(state),
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'advancedReportingEnabled'),
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'reportTaxDetailBusiness'),
    (state) => businessSelectors.getOpenBusinesses(state),
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'reportTaxDetailPersonal'),
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'reportGridGraph'),
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'reportsComparisonReportsRelease'),
  ],
  (
    reportConfiguration,
    businessEnabled,
    advancedReporting,
    reportTaxDetailBusiness,
    openBusinesses,
    reportTaxDetailPersonal,
    reportsGridGraph,
    reportsComparisonReports,
  ) => {
    if (!reportConfiguration) return null;
    return getReportFieldConfig(
      reportConfiguration,
      businessEnabled,
      Boolean(advancedReporting),
      Boolean(reportTaxDetailBusiness),
      openBusinesses?.toArray() ?? [],
      Boolean(reportTaxDetailPersonal),
      Boolean(reportsGridGraph),
      Boolean(reportsComparisonReports),
    );
  },
);
// Field-specific selectors that extract only needed data
export const selectGroupByConfig = createSelector(
  [(state, reportConfigId: string) => selectReportFieldConfig(state, reportConfigId)],
  (config) =>
    config
      ? {
          showGroupBy: config.showGroupBy,
          groupByOptions: config.groupByOptions,
          defaultGroupByOption: config.defaultGroupByOption,
        }
      : null,
);

export const selectUsageConfig = createSelector(
  [(state, reportConfigId: string) => selectReportFieldConfig(state, reportConfigId)],
  (config) =>
    config
      ? {
          showUsage: config.showUsage,
          usageOptions: config.usageOptions,
          defaultUsageOptions: config.defaultUsageOptions,
          usageSingleSelect: config.usageSingleSelect,
        }
      : null,
);

export const selectSegmentByConfig = createSelector(
  [(state, reportConfigId: string) => selectReportFieldConfig(state, reportConfigId)],
  (config) =>
    config
      ? {
          showSegmentBy: config.showSegmentBy,
          segmentByOptions: config.segmentByOptions,
          defaultSegmentByOption: config.defaultSegmentByOption,
          disableSegmentByForNetWorth: config.disableSegmentByForNetWorth,
        }
      : null,
);

export const selectTimestepConfig = createSelector(
  [(state, reportConfigId: string) => selectReportFieldConfig(state, reportConfigId)],
  (config) =>
    config
      ? {
          showTimestep: config.showTimestep,
          timestepOptions: config.timestepOptions,
          defaultTimestepOption: config.defaultTimestepOption,
          disableTimestepForNetWorth: config.disableTimestepForNetWorth,
        }
      : null,
);

/**
 * Get timestep options for the submenu, regardless of current columnType selection.
 * Used when showing Time as a nested menu option with interval choices.
 */
export const selectTimestepOptionsForSubmenu = createSelector(
  [(state, reportConfigId: string) => getReportConfigById(state, { id: reportConfigId })],
  (reportConfig) =>
    reportConfig ? getTimestepOptionsForSubtype(reportConfig.reportSubType, reportConfig.reportType) : [],
);

export const selectReportTypeConfig = createSelector(
  [(state, reportConfigId: string) => selectReportFieldConfig(state, reportConfigId)],
  (config) =>
    config
      ? {
          showReportType: config.showReportType,
          reportTypeOptions: config.reportTypeOptions,
          defaultReportTypeOption: config.defaultReportTypeOption,
        }
      : null,
);

export const selectReportSubTypeConfig = createSelector(
  [(state, reportConfigId: string) => selectReportFieldConfig(state, reportConfigId)],
  (config) =>
    config
      ? {
          showReportSubType: config.showReportSubType,
          reportSubTypeOptions: config.reportSubTypeOptions,
          defaultReportSubTypeOption: config.defaultReportSubTypeOption,
        }
      : null,
);

export const selectViewTypeConfig = createSelector(
  [(state, reportConfigId: string) => selectReportFieldConfig(state, reportConfigId)],
  (config) =>
    config
      ? {
          showViewType: config.showViewType,
          viewTypeOptions: config.viewTypeOptions,
          defaultViewTypeOption: config.defaultViewTypeOption,
        }
      : null,
);

export const selectReportVisibilityConfig = createSelector(
  [(state, reportConfigId: string) => selectReportFieldConfig(state, reportConfigId)],
  (config) =>
    config
      ? {
          showDateRange: config.showDateRange,
          showBalanceAsOf: config.showBalanceAsOf,
          showFilters: config.showFilters,
          showComparison: config.showComparison,
        }
      : null,
);

/**
 * Selector to validate report configuration
 */
export const selectIsValidReportConfig = createSelector(
  [
    (state, reportConfiguration: ReportConfiguration) => reportConfiguration,
    (state) => entitlementsSelectors.getBusinessEnabled(state),
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'advancedReportingEnabled'),
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'reportTaxDetailBusiness'),
    (state) => businessSelectors.getOpenBusinesses(state)?.toArray() ?? [],
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'reportTaxDetailPersonal'),
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'reportGridGraph'),
  ],
  (
    reportConfiguration,
    businessEnabled,
    advancedReporting,
    reportTaxDetailBusiness,
    openBusinesses,
    reportTaxDetailPersonal,
    reportsGridGraph,
  ) => {
    const config = getReportFieldConfig(
      reportConfiguration,
      businessEnabled,
      Boolean(advancedReporting),
      Boolean(reportTaxDetailBusiness),
      openBusinesses,
      Boolean(reportTaxDetailPersonal),
      Boolean(reportsGridGraph),
    );

    const reportSubType = reportConfiguration.reportSubType;
    const groupBy = reportConfiguration.rowType;
    const segmentBy = reportConfiguration.columnType;

    if (!config.allowedSubtypes[reportSubType]) return false;
    if (!config.allowedGroupBy[groupBy]) return false;
    if (segmentBy && !config.allowsSegmentBy && segmentBy !== 'NONE') return false;
    if (segmentBy && groupBy === segmentBy && segmentBy !== 'NONE') return false;

    if (
      reportConfiguration.reportType === 'COMPARISON' &&
      validateComparisonRanges(reportConfiguration.comparisonRanges, reportConfiguration.primaryAdditionalColumnTypes)
        .length > 0
    ) {
      return false;
    }

    return true;
  },
);

/**
 * Ordered comparison ranges for a report configuration.
 *
 * Reference-stable by construction: the array lives directly on the Immutable Record, so reading it
 * returns the same identity until the config itself changes, and configs without ranges share the
 * frozen `EMPTY_COMPARISON_RANGES`. This is deliberately a plain function rather than a
 * `createSelector` — a memoized selector whose result function just returns its input trips
 * reselect's dev-mode `identityFunctionCheck`, which the repo's fail-on-console Jest setup turns
 * into a test failure, and the memoization would buy nothing over the Record's own stability.
 */
export const selectComparisonRanges = (
  _state: unknown,
  reportConfiguration: ReportConfiguration,
): readonly ComparisonRange[] => reportConfiguration.comparisonRanges ?? EMPTY_COMPARISON_RANGES;

export const selectComparisonRangeById = createSelector(
  [
    (_state: unknown, props: { reportConfiguration: ReportConfiguration; rangeId: string }) =>
      props.reportConfiguration.comparisonRanges,
    (_state: unknown, props: { rangeId: string }) => props.rangeId,
  ],
  (comparisonRanges, rangeId): ComparisonRange | undefined =>
    comparisonRanges?.find((range) => range.rangeId === rangeId),
);

/** Filter configs by source=SAVED_REPORTS and persistent types, sorted by most recent. */
function filterSavedReportsConfigs(
  configsById: ReturnType<typeof getResourcesById>,
  persistentTypes: ReadonlySet<string>,
): ReportConfiguration[] {
  if (!configsById) return [];

  const results: ReportConfiguration[] = [];
  configsById.forEach((config) => {
    if (!config || config.isDeleted) return;
    if (persistentTypes.has(config.persistentType) && config.source === 'SAVED_REPORTS') {
      results.push(config);
    }
  });

  return results.sort((a, b) => (b.modifiedAt || '').localeCompare(a.modifiedAt || ''));
}

// Open/Temporary configs for the open/save tab bar (source=SAVED_REPORTS only)
export const selectSavedReportsOpenTabs = createSelector(getResourcesById, (configsById) =>
  filterSavedReportsConfigs(configsById, OPEN_PERSISTENT_TYPES),
);

// Saved/Custom configs for the open/save sidebar (source=SAVED_REPORTS only)
export const selectSavedReportsConfigs = createSelector(getResourcesById, (configsById) =>
  filterSavedReportsConfigs(configsById, SAVED_PERSISTENT_TYPES),
);

const getExistingSavedReportNames = (
  savedConfigs: readonly ReportConfiguration[],
  currentConfigId: string | undefined,
): string[] => savedConfigs.filter((config) => config.id !== currentConfigId).map(getConfigDisplayName);

export const selectSuggestedSavedReportName = createSelector(
  [getReportConfigById, selectSavedReportsConfigs],
  (reportConfig, savedConfigs) => {
    if (!reportConfig) {
      return '';
    }

    return getSuggestedSavedReportName(
      getConfigDisplayName(reportConfig),
      getExistingSavedReportNames(savedConfigs, reportConfig.id),
    );
  },
);

export const selectSuggestedSaveAsNewReportName = createSelector(
  [getReportConfigById, selectSavedReportsConfigs],
  (reportConfig, savedConfigs) => {
    if (!reportConfig) {
      return '';
    }

    return getSuggestedSavedReportName(
      getSavedReportBaseName(getConfigDisplayName(reportConfig)),
      getExistingSavedReportNames(savedConfigs, reportConfig.id),
    );
  },
);

export const getReportConfigFieldById = createSelector(
  getReportConfigById,
  (_state: unknown, props: { id: string; fieldName: keyof ReportConfigurationProps }) => props.fieldName,
  (reportConfiguration, fieldName) => reportConfiguration?.get(fieldName),
);
